/* eslint-disable max-lines */
import moment from 'moment-timezone';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import {
    AddCalendarDocument,
    AddSchedulesDocument,
    ArgoCalendarEventConstraint,
    ArgoCalendarEventInsertInput,
    ArgoCalendarEventUdfDataConstraint,
    ArgoCalendarEventUdfDataInsertInput,
    ArgoCalendarEventUdfDataUpdateColumn,
    type ArgoCalendarEventUdfDefaultData,
    ArgoCalendarEventUdfDefaultDataConstraint,
    ArgoCalendarEventUdfDefaultDataUpdateColumn,
    ArgoCalendarEventUpdateColumn,
    ArgoScheduleDayEnum,
    type ArgoScheduleEvent,
    ArgoScheduleEventConstraint,
    ArgoScheduleEventUpdateColumn,
    ArgoScheduleInsertInput,
    ArgoScheduleModeEnum,
    CalendarEventFragment,
    GetUdfColumnsDocument,
    ScheduleEventFragment,
    type ScheduleFragment,
    UpdateCalendarDocument,
    UpdateScheduleDocument,
} from 'types/db';
import { CalendarFormatter } from 'vuetify';
import colors from 'vuetify/es5/util/colors';
import { getListChanges } from 'src/util/composables/watch-list-changes';
import { UDFColumn, UDFData } from 'src/udf/udf.validations';
import { toDatetimeLocalFormat } from 'src/util/composables/useDatetimeInput';
import { validateCalendarEntity } from './calendars/calendar.validations';
import type Vue from 'vue';
import { type EntityModifyParameters, type ExtendedEntityParameters } from 'src/components/EntityDetail';
import type { EventFromModal } from './EventCreateModal.vue';
import type { EventFromEventSelector } from 'src/components/EventSelector';
import type { ValidationResult } from 'types';
import { validateSchedule } from './schedule.validations';

export type ScheduleEvent = Pick<ArgoScheduleEvent, 'id' | 'range' | 'day'> & {
    event: EventFromEventSelector;
    // NOTE: This is the type referenced by the GraphQL schema, but why is it Calendar udf data and not Schedule udf data?
    udf_default_data: Pick<ArgoCalendarEventUdfDefaultData, 'id' | 'num_val' | 'str_val' | 'udf_column_id' | 'meta'>[];
};

export type ScheduleEntity = {
    schedule: Pick<ScheduleFragment, 'id' | 'name' | 'mode' | 'enabled'> & {
        schedule_events: ScheduleEvent[];
    };
};


export const VCalendarFormat = [ 1, 2, 3, 4, 5, 6, 0 ];
export const scheduleDays = [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday' ];

// we use local time as the default for front end so that it makessense for the user to configure
export const defaultStartDate = '1996-01-01T00:00';

export function formatAMPM (date: any) {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours %= 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    const strTime = `${hours}:${minutes} ${ampm}`;
    return strTime;
}
export type CalendarComponent = Vue & { prev: () => void; next: () => void; checkChange: () => void; getFormatter: (format: any) => CalendarFormatter };

export type VEvent = {
    id: string;
    priority: number;
    textColor?: string;
    name: string;
    start: Date;
    end: Date;
    timed: boolean;
    color?: string;
    meta?: Record<string, unknown>;
};

export type VCalendarEvent = {
    event_id: string;
    event_type_id: number | string;
    udf_data: UDFData[];
    day: ArgoScheduleDayEnum;
} & VEvent;

export function fromVCalToISOTs (date: Date) {
    return date.toISOString().substring(0, 16);
}

export const convertToShortISOFormat = toDatetimeLocalFormat; // aliased to avoid big refactor

export function extractCalendarEventMeta (event: ScheduleEventFragment['event'] | EventFromModal['eventCfg']['event']) {
    if (event?.meta) {
        if (typeof event.meta === 'object') {
            return event.meta;
        } else if (typeof event.meta === 'string') {
            return JSON.parse(event.meta);
        }
    }
    return {};
}
export function getTotalAvailableDays (scheduleMode: ArgoScheduleModeEnum) {
    switch (scheduleMode) {
        case 'biweekly':
            return 14;
        default:
            return 7;
    }
}

export async function getUDFColumnsForEventType (etypeId : number): Promise<UDFColumn[]> {
    const result = await gqlClient.request({
        document: GetUdfColumnsDocument,
        variables: {
            filter: {
                event_type: {
                    event_type_id: {
                        _eq: etypeId,
                    },
                },
            },
        },
    });
    return result.udfColumns;
}

export function scheduleEventToVCalendarEvent (scheduleEvents: (ScheduleEvent | ScheduleEventFragment)[]): VCalendarEvent[] {
    return scheduleEvents.map((event) => {
        const timeStart = event.range.replace(')', ']');
        const timeEnd = event.range.replace(')', ']');
        const startDate = new Date(`${JSON.parse(timeStart)[0]}`);
        const endDate = new Date(`${JSON.parse(timeEnd)[1]}`);
        const eventName = event.event?.name || '';
        const meta = extractCalendarEventMeta(event.event);
        const color = meta?.identifier?.backgroundColor;
        const textColor = meta?.identifier?.textColor;
        return {
            id: event.id,
            event_id: event.event?.id || '',
            event_type_id: event.event?.event_type?.id || 0,
            udf_data: 'udf_default_data' in event ? event.udf_default_data : event.udf_data,
            name: eventName,
            start: startDate,
            end: endDate,
            color: color ? color : colors.grey.darken2,
            textColor: textColor ? textColor : 'white',
            day: event.day,
            timed: true,
            priority: event.event?.event_type?.priority || 0,
        };
    });
}

function fromVCalEventToCalendarEvent (event: Partial<VCalendarEvent>, timezone: string): ArgoCalendarEventInsertInput {
    // Conversion flow:
    // 1. The Date Class instantiates a date object in the brower's local timezone
    // 2. The timezone of the calendar is substituted in-place of the local timezone
    // 3. The date is converted to ISO string
    const startDate = moment.tz(moment(event.start).format('YYYY-MM-DD HH:mm'), timezone).toISOString().substr(0, 16);
    const endDate = moment.tz(moment(event.end).format('YYYY-MM-DD HH:mm'), timezone).toISOString().substr(0, 16);

    return {
        day: event.day,
        event_id: event.event_id,
        schedule_event_id: event.meta?.fromScheduleEvent as string || null,
        id: event.id,
        range: `["${startDate}", "${endDate}")`,
        udf_data: {
            data: event.udf_data?.map(deeta => ({
                str_val: deeta.str_val,
                num_val: deeta.num_val,
                id: deeta.id,
                udf_column_id: deeta.udf_column_id,
            })) || [],
            on_conflict: {
                constraint: ArgoCalendarEventUdfDataConstraint.CalendarEventUdfDataPkey,
                update_columns: [ 'num_val', 'str_val', 'meta' ] as ArgoCalendarEventUdfDataUpdateColumn[],
            },
        },
    };
}

export function parseArgoCalendarEvents (dbCalendarEvents: CalendarEventFragment[], timezone: string): VCalendarEvent[] {
    return dbCalendarEvents?.map<VCalendarEvent>((event) => {
        const times = event.range.replace(')', ']');

        // Conversion flow:
        // 1. The DB stores dates in UTC
        // 2. The calendar needs to display dates in the calendar's timezone
        // 3. The Date class instantiates a date object in the brower's local timezone
        const startDate = new Date(moment.tz(`${JSON.parse(times)[0]}`, 'UTC').tz(timezone).format('YYYY-MM-DD HH:mm'));
        const endDate = new Date(moment.tz(`${JSON.parse(times)[1]}`, 'UTC').tz(timezone).format('YYYY-MM-DD HH:mm'));

        const eventName = event.event?.name || '';
        const meta = extractCalendarEventMeta(event.event);
        const color = meta?.identifier?.backgroundColor;
        const textColor = meta?.identifier?.textColor;
        const calendarEvent: VCalendarEvent = {
            id: event.id,
            event_id: event.event?.id || '',
            event_type_id: event.event?.event_type?.id || 0,
            udf_data: event.udf_data,
            name: eventName,
            start: startDate,
            end: endDate,
            color: color ? color : 'gray',
            priority: event.event?.event_type?.priority || 0,
            day: event.day,
            timed: true,
            textColor: textColor ? textColor : 'white',
        };
        return calendarEvent;
    });
}

export async function updateCalendar (payload: ExtendedEntityParameters<'calendar'>): Promise<ValidationResult> {
    const { entity, oldEntity } = payload;
    const validationResult = await validateCalendarEntity(entity, oldEntity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        await gqlClient.request({
            document: UpdateCalendarDocument,
            variables: {
                calendar: {
                    name: entity.calendar.name,
                    description: entity.calendar.description,
                    enabled: entity.calendar.enabled,
                    schedule_id: entity.calendar.schedule_id,
                    is_default: entity.calendar.is_default,
                    timezone: entity.calendar.timezone,
                    calendar_events: {
                        data: [ ...entity.calendar_events.inserts.map(e => fromVCalEventToCalendarEvent(e, entity.calendar.timezone)), ...entity.calendar_events.updates.map(e => fromVCalEventToCalendarEvent(e, entity.calendar.timezone)) ],
                        on_conflict: {
                            constraint: 'calendar_event_pkey' as ArgoCalendarEventConstraint,
                            update_columns: [ 'range', 'day' ] as ArgoCalendarEventUpdateColumn[],
                        },
                    },
                },
                deleteCalEvents: entity.calendar_events.deletes.map(e => e.id),
            },
        });

        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Calendar: "${JSON.stringify(err)}"`,
        };
    }
}
export async function createCalendar (payload: EntityModifyParameters<'calendar'>): Promise<ValidationResult> {
    const entity = payload.entity;
    
    const validationResult = await validateCalendarEntity(payload.entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    try {
        await gqlClient.request({
            document: AddCalendarDocument,
            variables: {
                calendar: {
                    id: entity.calendar.id,
                    name: entity.calendar.name,
                    description: entity.calendar.description,
                    enabled: entity.calendar.enabled,
                    schedule_id: entity.calendar.schedule_id || undefined,
                    is_default: entity.calendar.is_default,
                    timezone: entity.calendar.timezone,
                    calendar_events: {
                        data: [ ...entity.calendar_events.inserts.map(e => fromVCalEventToCalendarEvent(e, entity.calendar.timezone)), ...entity.calendar_events.updates.map(e => fromVCalEventToCalendarEvent(e, entity.calendar.timezone)) ],
                        on_conflict: {
                            constraint: 'calendar_event_pkey' as ArgoCalendarEventConstraint,
                            update_columns: [ 'range', 'day' ] as ArgoCalendarEventUpdateColumn[],
                        },
                    },
                },
            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Calendar: "${JSON.stringify(err)}"`,
        };
    }
}

export async function updateSchedule (payload: ExtendedEntityParameters<'schedule'>): Promise<ValidationResult> {
    const {
        entity,
        oldEntity: originalOldEntity,
    } = payload;

    const validationResult = validateSchedule(entity.schedule);
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    const eventDifferences = getListChanges(entity.schedule.schedule_events, originalOldEntity?.schedule.schedule_events || [], { format: 'plc', deep: true, partialChanges: false });
    try {
        const schedule: ArgoScheduleInsertInput = {
            id: entity.schedule.id,
            name: entity.schedule.name,
            mode: entity.schedule.mode,
            enabled: entity.schedule.enabled,
            schedule_events: {
                data: [ ...eventDifferences.inserts, ...eventDifferences.updates ].map(e => ({
                    id: e.id,
                    event_id: e.event.id,
                    range: e.range,
                    day: e.day as ArgoScheduleDayEnum,
                    udf_default_data: {
                        data: e.udf_default_data.map<ArgoCalendarEventUdfDataInsertInput>(deeta => ({
                            num_val: deeta.num_val,
                            str_val: deeta.str_val,
                            id: deeta.id,
                            udf_column_id: deeta.udf_column_id,
                            meta: deeta.meta,
                        })),
                        on_conflict: {
                            constraint: ArgoCalendarEventUdfDefaultDataConstraint.CalendarEventUdfDefaultDataPkey,
                            update_columns: [ 'num_val', 'str_val', 'meta' ] as ArgoCalendarEventUdfDefaultDataUpdateColumn[],
                        },
                    },
                })),
                on_conflict: {
                    constraint: ArgoScheduleEventConstraint.ScheduleEventPkey,
                    update_columns: [ 'day', 'range', 'event_id' ] as ArgoScheduleEventUpdateColumn[],
                },
            },
        };
        await gqlClient.request({
            document: UpdateScheduleDocument,
            variables: {
                schedule,
                deleteScheduleEvents: eventDifferences.deletes.map(e => e.id),

            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Schedule: "${JSON.stringify(err)}"`,
        };
    }
}

export async function createSchedule (payload: EntityModifyParameters<'schedule'>): Promise<ValidationResult> {

    const entity = payload.entity;

    const validationResult = validateSchedule(entity.schedule);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        await gqlClient.request({
            document: AddSchedulesDocument,
            variables: {
                schedule: {
                    id: entity.schedule.id,
                    name: entity.schedule.name,
                    mode: entity.schedule.mode,
                    enabled: entity.schedule.enabled,
                    schedule_events: {
                        data: entity.schedule.schedule_events.map(e => ({
                            // what is the difference between id and event_id?
                            id: e.id,
                            event_id: e.event.id,
                            range: e.range,
                            day: e.day as ArgoScheduleDayEnum,
                            udf_default_data: {
                                data: e.udf_default_data.map<ArgoCalendarEventUdfDataInsertInput>(deeta => ({
                                    num_val: deeta.num_val,
                                    str_val: deeta.str_val,
                                    id: deeta.id,
                                    udf_column_id: deeta.udf_column_id,
                                    meta: deeta.meta,
                                })),
                                on_conflict: {
                                    constraint: ArgoCalendarEventUdfDefaultDataConstraint.CalendarEventUdfDefaultDataPkey,
                                    update_columns: [ 'num_val', 'str_val', 'meta' ] as ArgoCalendarEventUdfDefaultDataUpdateColumn[],
                                },
                            },
                        })),
                        on_conflict: {
                            constraint: ArgoScheduleEventConstraint.ScheduleEventPkey,
                            update_columns: [ 'day', 'range', 'event_id' ] as ArgoScheduleEventUpdateColumn[],
                        },
                    },
                },
            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Schedule: "${JSON.stringify(err)}"`,
        };
    }
}
